import { Col, message, Row, Space, Spin, Tag } from "antd";
import { FileExcelOutlined, FilePdfOutlined } from "@ant-design/icons";
import axios from "axios";
import moment from "moment";
import React, { Component } from "react";
import { connect } from "react-redux";
import { ResponsiveBar } from "@nivo/bar";
import { ResponsivePie } from "@nivo/pie";
import { ButtonItem, ErrorItem, Form, TableItemDinamico } from "../../../../Components/Items";
import FiltrosCalidad from "./FiltrosCalidad";
import { descargarPdfCalidad, exportarExcel, fmtFecha } from "./exportCalidad";
import {
  ChartCard,
  colorPorcentaje,
  EstadoVacio,
  isMobile,
  KpiRow,
  NIVO_THEME,
  tagPorcentaje,
  TooltipCalidad,
} from "./reporteCalidadStyles";

const PERIODO_INICIAL = { periodo: [moment().subtract(1, "month"), moment()] };

const COLOR_BUCKET = {
  "100%": "#10b981",
  "80-99%": "#22d3ee",
  "50-79%": "#f59e0b",
  "< 50%": "#ef4444",
};

const recortar = (texto, largo) =>
  texto && texto.length > largo ? `${texto.substring(0, largo)}…` : texto;

/**
 * Reporte de criterios de aceptación: agrupa cada criterio evaluado en los registros del
 * período y los ordena por tasa de cumplimiento ascendente — los que más fallan primero.
 */
class ReporteCriterios extends Component {
  formRef = React.createRef();
  tableRef = null;

  state = {
    loading: false,
    criterios: [],
    totalCriterios: 0,
    resumen: null,
    empresa: null,
    currentFilters: {},
  };

  buildPayload(values = {}) {
    const payload = {};
    if (values.programaId) payload.programaId = values.programaId;
    if (values.procesoId) payload.procesoId = values.procesoId;
    if (values.resultado) payload.resultado = values.resultado;
    if (values.periodo?.length === 2) {
      payload.fechaInicio = values.periodo[0].startOf("day").toISOString();
      payload.fechaFin = values.periodo[1].endOf("day").toISOString();
    }
    return payload;
  }

  onFetchData(params) {
    this.setState({ loading: true });
    axios
      .post(
        "programa/reporteCriterios",
        { empresaId: this.props.empresaId, ...params },
        { headers: this.props.headersToken }
      )
      .then((res) => {
        if (res.data.success === 2) {
          this.setState({
            criterios: res.data.data,
            totalCriterios: res.data.totalRecords,
            resumen: res.data.resumen,
            empresa: res.data.empresa ?? null,
          });
        } else {
          message.error(res.data.message ?? "No se pudo generar el reporte");
        }
      })
      .catch(() => message.error("Error de conexión"))
      .finally(() => this.setState({ loading: false }));
  }

  consultarReporte(values) {
    const payload = this.buildPayload(values);
    this.setState({ currentFilters: payload });
    this.tableRef?.updateExternalFilters(payload);
  }

  async fetchAll() {
    const res = await axios.post(
      "programa/reporteCriterios",
      { empresaId: this.props.empresaId, ...this.state.currentFilters },
      { headers: this.props.headersToken }
    );
    if (res.data.success !== 2) throw new Error("Error al obtener datos");
    return res.data;
  }

  async descargarExcel() {
    this.setState({ loading: true });
    try {
      const { data: criterios } = await this.fetchAll();
      exportarExcel(
        "Reporte_Criterios_Calidad",
        "Criterios",
        criterios.map((c) => ({
          Criterio: c.criterio,
          "Punto de control": c.punto,
          Proceso: c.proceso,
          Programa: c.programa,
          Evaluaciones: c.evaluaciones,
          Cumplidos: c.cumplidos,
          "No cumplidos": c.noCumplidos,
          "% Cumplimiento": `${c.porcentaje}%`,
          "Última evaluación": fmtFecha(c.ultimaEvaluacion),
          Observaciones: c.observaciones.map((o) => o.observacion).join(" | "),
        }))
      );
    } catch {
      message.error("No se pudo generar el Excel");
    } finally {
      this.setState({ loading: false });
    }
  }

  async descargarPdf() {
    this.setState({ loading: true });
    try {
      const { data: criterios, resumen, empresa } = await this.fetchAll();
      const peores = resumen?.peores ?? [];

      const columnas = [
        "Criterio",
        "Punto de control",
        "Proceso",
        "Evaluaciones",
        "Cumplidos",
        "No cumplidos",
        "%",
      ];
      const widths = ["*", "*", "*", 60, 50, 62, 34];
      const fila = (c) => [
        c.criterio,
        c.punto,
        c.proceso,
        { text: `${c.evaluaciones}`, alignment: "center" },
        { text: `${c.cumplidos}`, alignment: "center" },
        { text: `${c.noCumplidos}`, alignment: "center", bold: c.noCumplidos > 0 },
        { text: `${c.porcentaje}%`, alignment: "center", bold: true },
      ];

      descargarPdfCalidad({
        empresa,
        titulo: "Reporte de Criterios",
        subtitulo: "Criterios de Aceptación",
        extras: [`Criterios distintos: ${criterios.length}`],
        nombreArchivo: "Reporte_Criterios_Calidad",
        kpis: [
          { label: "Criterios", value: resumen?.criteriosDistintos ?? 0 },
          { label: "Evaluaciones", value: resumen?.totalEvaluaciones ?? 0 },
          { label: "Cumplidos", value: resumen?.totalCumplidos ?? 0 },
          { label: "No cumplidos", value: resumen?.totalNoCumplidos ?? 0 },
          { label: "% Global", value: `${resumen?.porcentajeGlobal ?? 0}%` },
          { label: "Graves (<50%)", value: resumen?.graves ?? 0 },
          { label: "Críticos (<80%)", value: resumen?.criticos ?? 0 },
          { label: "Registros", value: resumen?.registrosEvaluados ?? 0 },
        ],
        secciones: [
          {
            titulo: `Top ${peores.length} criterios con más fallas (de ${criterios.length})`,
            columnas,
            widths,
            filas: peores.map(fila),
            vacio: "Todos los criterios se cumplieron al 100%.",
          },
          {
            titulo: `Todos los criterios (${criterios.length})`,
            columnas,
            widths,
            filas: criterios.map(fila),
          },
          {
            titulo: "Observaciones de criterios no cumplidos",
            columnas: ["Criterio", "Punto de control", "Observación", "Fecha"],
            widths: ["*", "*", "*", 62],
            filas: criterios.flatMap((c) =>
              c.observaciones.map((o) => [c.criterio, c.punto, o.observacion, fmtFecha(o.fecha)])
            ),
            vacio: "Sin observaciones registradas en el período.",
          },
        ],
      });
    } catch {
      message.error("No se pudo generar el PDF");
    } finally {
      this.setState({ loading: false });
    }
  }

  kpis() {
    const { resumen } = this.state;
    if (!resumen) return [];
    return [
      { key: "criterios", label: "Criterios distintos", value: resumen.criteriosDistintos, color: "#0f172a" },
      { key: "evaluaciones", label: "Evaluaciones", value: resumen.totalEvaluaciones, color: "#00BFD6" },
      { key: "cumplidos", label: "Cumplidos", value: resumen.totalCumplidos, color: "#10b981" },
      { key: "noCumplidos", label: "No cumplidos", value: resumen.totalNoCumplidos, color: "#ef4444" },
      {
        key: "global",
        label: "% Global",
        value: `${resumen.porcentajeGlobal}%`,
        color: colorPorcentaje(resumen.porcentajeGlobal),
      },
      {
        key: "graves",
        label: "Graves (<50%)",
        value: resumen.graves ?? 0,
        color: resumen.graves > 0 ? "#ef4444" : "#10b981",
        hint: `${resumen.criticos} por debajo del 80%`,
      },
    ];
  }

  graficas() {
    const { resumen } = this.state;
    if (!resumen || resumen.totalEvaluaciones === 0) return null;

    const movil = isMobile();

    const dataPeores = [...(resumen.peores ?? [])].reverse().map((c) => ({
      criterio: recortar(c.criterio, movil ? 18 : 34),
      criterioCompleto: c.criterio,
      punto: c.punto,
      evaluaciones: c.evaluaciones,
      noCumplidos: c.noCumplidos,
      porcentaje: c.porcentaje,
    }));

    const dataDist = (resumen.distribucion ?? [])
      .filter((d) => d.cantidad > 0)
      .map((d) => ({ id: d.rango, label: d.rango, value: d.cantidad }));

    if (dataPeores.length === 0 && dataDist.length === 0) return null;

    return (
      <Row gutter={[14, 14]} style={{ marginBottom: 14 }}>
        {dataPeores.length > 0 && (
          <Col xs={24} md={dataDist.length ? 15 : 24}>
            <ChartCard
              label="Prioridad"
              titulo={`Top ${dataPeores.length} criterios con más fallas`}
              pill={`de ${resumen.criteriosDistintos}`}
              accent="linear-gradient(90deg, #ef4444, #f59e0b)"
              delay={0.06}
              altura={Math.max(230, dataPeores.length * 32 + 60)}
            >
              <ResponsiveBar
                data={dataPeores}
                keys={["porcentaje"]}
                indexBy="criterio"
                layout="horizontal"
                margin={{ top: 8, right: 44, bottom: 40, left: movil ? 120 : 210 }}
                padding={0.3}
                maxValue={100}
                colors={({ data }) => colorPorcentaje(data.porcentaje)}
                borderRadius={4}
                axisTop={null}
                axisRight={null}
                axisBottom={{ tickSize: 0, tickPadding: 8, format: (v) => `${v}%` }}
                axisLeft={{ tickSize: 0, tickPadding: 8 }}
                enableGridY={false}
                gridXValues={[0, 25, 50, 75, 100]}
                label={(d) => `${d.value}%`}
                labelSkipWidth={30}
                labelTextColor="#ffffff"
                theme={NIVO_THEME}
                tooltip={({ data }) => (
                  <TooltipCalidad
                    color={colorPorcentaje(data.porcentaje)}
                    titulo={data.criterioCompleto}
                    filas={[
                      { label: "Punto", value: data.punto },
                      { label: "Evaluaciones", value: data.evaluaciones },
                      { label: "No cumplidos", value: data.noCumplidos },
                      { label: "Cumplimiento", value: `${data.porcentaje}%` },
                    ]}
                  />
                )}
              />
            </ChartCard>
          </Col>
        )}

        {dataDist.length > 0 && (
          <Col xs={24} md={dataPeores.length ? 9 : 24}>
            <ChartCard
              label="Distribución"
              titulo="Criterios por nivel"
              pill={`${this.state.resumen.criteriosDistintos} criterios`}
              accent="linear-gradient(90deg, #00BFD6, #22d3ee)"
              delay={0.12}
              altura={250}
            >
              <ResponsivePie
                data={dataDist}
                margin={
                  movil
                    ? { top: 10, right: 10, bottom: 40, left: 10 }
                    : { top: 10, right: 20, bottom: 46, left: 20 }
                }
                colors={({ id }) => COLOR_BUCKET[id] ?? "#94a3b8"}
                innerRadius={0.67}
                padAngle={1.8}
                cornerRadius={6}
                activeOuterRadiusOffset={9}
                borderWidth={0}
                enableArcLabels={false}
                arcLinkLabelsSkipAngle={movil ? 360 : 14}
                arcLinkLabelsTextColor="#94a3b8"
                arcLinkLabelsThickness={1}
                arcLinkLabelsColor={{ from: "color" }}
                theme={NIVO_THEME}
                legends={[
                  {
                    anchor: "bottom",
                    direction: "row",
                    translateY: 42,
                    itemsSpacing: 6,
                    itemWidth: movil ? 62 : 74,
                    itemHeight: 16,
                    itemTextColor: "#64748b",
                    symbolSize: 9,
                    symbolShape: "circle",
                    effects: [{ on: "hover", style: { itemTextColor: "#0f172a" } }],
                  },
                ]}
                tooltip={({ datum }) => (
                  <TooltipCalidad
                    color={datum.color}
                    titulo={`Cumplimiento ${datum.label}`}
                    filas={[{ label: "Criterios", value: datum.value }]}
                  />
                )}
              />
            </ChartCard>
          </Col>
        )}
      </Row>
    );
  }

  columnas() {
    return [
      {
        title: "Criterio",
        dataIndex: "criterio",
        ellipsis: true,
      },
      {
        title: "Punto de control",
        dataIndex: "punto",
        ellipsis: true,
        width: 170,
      },
      {
        title: "Proceso",
        dataIndex: "proceso",
        ellipsis: true,
        width: 150,
      },
      {
        title: "Programa",
        dataIndex: "programa",
        ellipsis: true,
        width: 150,
      },
      {
        title: "Eval.",
        dataIndex: "evaluaciones",
        align: "center",
        width: 70,
      },
      {
        title: "Cumplidos",
        dataIndex: "cumplidos",
        align: "center",
        width: 95,
      },
      {
        title: "No cumpl.",
        dataIndex: "noCumplidos",
        align: "center",
        width: 95,
        render: (v) => (v > 0 ? <span style={{ color: "#ef4444", fontWeight: 600 }}>{v}</span> : v),
      },
      {
        title: "% Cumpl.",
        dataIndex: "porcentaje",
        align: "center",
        width: 100,
        render: (v) => <Tag color={tagPorcentaje(v)}>{v}%</Tag>,
      },
      {
        title: "Última eval.",
        dataIndex: "ultimaEvaluacion",
        width: 120,
        render: fmtFecha,
      },
    ];
  }

  render() {
    const { criterios, totalCriterios, resumen, loading } = this.state;

    return (
      <Form
        ref={this.formRef}
        name="reporteCalidadCriterios"
        onFinish={this.consultarReporte.bind(this)}
        onFinishFailed={ErrorItem}
        initialValues={PERIODO_INICIAL}
      >
        <Spin spinning={loading} wrapperClassName="cal-spin">
          <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 12 }}>
            <Space>
              {resumen && (
                <ButtonItem verde onClick={() => this.descargarExcel()} icon={<FileExcelOutlined />}>
                  Excel
                </ButtonItem>
              )}
              {resumen && (
                <ButtonItem rojo onClick={() => this.descargarPdf()} icon={<FilePdfOutlined />}>
                  PDF
                </ButtonItem>
              )}
              <ButtonItem type="primary" htmlType="submit">
                Consultar
              </ButtonItem>
            </Space>
          </div>

          <FiltrosCalidad
            campos={["periodo", "programa", "proceso", "resultado"]}
            formRef={this.formRef}
          />

          <KpiRow items={this.kpis()} />

          {this.graficas()}

          {resumen && resumen.criteriosDistintos === 0 && (
            <EstadoVacio
              titulo="Sin criterios evaluados en el período"
              texto="No se capturaron criterios de aceptación en los registros que coinciden con los filtros."
            />
          )}

          <div style={{ display: resumen && resumen.criteriosDistintos === 0 ? "none" : "block" }}>
            <TableItemDinamico
              onRef={(ref) => (this.tableRef = ref)}
              totalRecords={totalCriterios}
              onFetchData={this.onFetchData.bind(this)}
              loading={loading}
              size="small"
              dataSource={criterios}
              columns={this.columnas()}
              rowKey="id"
              expandable={{
                rowExpandable: (r) => r.observaciones?.length > 0,
                expandedRowRender: (r) => (
                  <div style={{ paddingLeft: 8 }}>
                    <div
                      style={{
                        fontSize: 11,
                        fontWeight: 700,
                        letterSpacing: "0.08em",
                        textTransform: "uppercase",
                        color: "#94a3b8",
                        marginBottom: 6,
                      }}
                    >
                      Observaciones de los no cumplidos
                    </div>
                    {r.observaciones.map((o, i) => (
                      <div key={i} style={{ fontSize: 12, color: "#1e293b", marginBottom: 3 }}>
                        <span style={{ color: "#94a3b8", marginRight: 8 }}>{fmtFecha(o.fecha)}</span>
                        {o.observacion}
                      </div>
                    ))}
                  </div>
                ),
              }}
            />
          </div>
        </Spin>
      </Form>
    );
  }
}

const mapStateToProps = (state) => ({
  headersToken: state.user.headersToken,
  empresaId: state.empresa.id,
});

export default connect(mapStateToProps)(ReporteCriterios);
